import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";

import DataHandler from "config/DataHandler";
import StorageHandler from "config/StorageHandler";

import { storageKeys } from "settings";

const settingsContext = createContext();

const defaultSettings = {
  loaded: false,
  lang: {
    code: "",
    country: "",
    langFile: "",
  },
  appInfo: {
    appName: "",
    backgroundImage: "",
    backgroundImages: [],
    backgroundVideo: "",
    generalBackgroundImage: "",
    landingQuote: "",
    loginImage: "",
    logo: "",
    logoWhite: "",
    logoWide: "",
    logoWideWhite: "",
    primaryColor: "",
    profileImageHeader: "",
    programBenefits: [],
    secondaryColor: "",
    socials: {
      tiktok: "",
      facebook: "",
      flickr: "",
      instagram: "",
      twitter: "",
      youtube: "",
      pinterest: "",
      googlePlus: "",
      linkedin: "",
    },
  },
};

const parseLang = (langResponse) => {
  const {
    code = defaultSettings.lang.code,
    country = defaultSettings.lang.country,
    langFile = defaultSettings.lang.langFile,
  } = langResponse;

  return {
    code,
    country,
    langFile,
  };
};

// takes the appInfo response and parses it into the proper state, defaulting to the defaultSettings if that data is not available.
const parseAppInfo = (appInfoResponse) => {
  const {
    appName = defaultSettings.appInfo.appName,
    backgroundImage = defaultSettings.appInfo.backgroundImage,
    backgroundImages = defaultSettings.appInfo.backgroundImages,
    backgroundVideo = defaultSettings.appInfo.backgroundVideo,
    generalBackgroundImage = defaultSettings.appInfo.generalBackgroundImage,
    landingQuote = defaultSettings.appInfo.landingQuote,
    loginImage = defaultSettings.appInfo.loginImage,
    logo = defaultSettings.appInfo.logo,
    logoWhite = defaultSettings.appInfo.logoWhite,
    logoWide = defaultSettings.appInfo.logoWide,
    logoWideWhite = defaultSettings.appInfo.logoWideWhite,
    primaryColor = defaultSettings.appInfo.primaryColor,
    profileImageHeader = defaultSettings.appInfo.profileImageHeader,
    programBenefits = defaultSettings.appInfo.programBenefits,
    secondaryColor = defaultSettings.appInfo.secondaryColor,
    socials = defaultSettings.appInfo.socials,
  } = appInfoResponse;

  return {
    appName,
    backgroundImage,
    backgroundImages,
    backgroundVideo,
    generalBackgroundImage,
    landingQuote,
    loginImage,
    logo,
    logoWhite,
    logoWide,
    logoWideWhite,
    primaryColor,
    profileImageHeader,
    programBenefits,
    secondaryColor,
    socials,
  };
};

/**
 * Provides a context for app settings information. This provider is meant to encapsulate the app for appwide use.
 *
 * @param {React.ReactNode} children - The children components that can consume the context.
 * @returns {JSX.Element} The provider component.
 */
const SettingsProvider = ({ children }) => {
  // defining the lang and appInfo initial states as empty objects, to prevent undefined errors when the app is first loaded.
  const [appSettings, setAppSettings] = useState(defaultSettings);

  // single fetch function for all settings data, saved to a single state. This is to prevent multiple fetches and thus multiple reloads when the app is first loaded.
  const fetchSettings = useCallback(async () => {
    const newAppId = await DataHandler.matchAppId();
    const newLang = await StorageHandler.load(storageKeys.lang);
    const newAppInfo = await StorageHandler.load(storageKeys.appInfo);

    const parsedLang = parseLang(newLang);
    const parsedAppInfo = parseAppInfo(newAppInfo);

    const newSettings = {
      loaded: true,
      appId: newAppId,
      lang: parsedLang,
      appInfo: parsedAppInfo,
    };

    setAppSettings(newSettings);
  }, []);

  const settingsValues = useMemo(
    () => ({
      ...appSettings,
      fetchSettings,
    }),
    [appSettings, fetchSettings]
  );

  return (
    <settingsContext.Provider value={settingsValues}>
      {children}
    </settingsContext.Provider>
  );
};

/**
 * @typedef {object} Lang
 * @property {string} code - The language abreviation code. String is capitalized. E.g.: 'EN'.
 * @property {string} country - The country of origin/use. E.g.: 'Netherlands'.
 * @property {string} langFile - The language file name. E.g.: 'en'.
 */

/**
 * @typedef {object} Socials
 * @property {string} tiktok - The tiktok url.
 * @property {string} facebook - The facebook url.
 * @property {string} flickr - The flickr url.
 * @property {string} instagram - The instagram url.
 * @property {string} twitter - The twitter url.
 * @property {string} youtube - The youtube url.
 * @property {string} pinterest - The pinterest url.
 * @property {string} googlePlus - The googlePlus url.
 * @property {string} linkedin - The linkedin url.
 */

/**
 * @typedef {object} AppInfo
 * @property {string} appName - The name of the app.
 * @property {string} backgroundImage - The background image url.
 * @property {string} backgroundVideo - The background video url.
 * @property {string} loginImage - The login image url.
 * @property {string} logo - The logo image url.
 * @property {string} logoWhite - The white logo image url.
 * @property {string} logoWide - The wide logo image url.
 * @property {string} logoWideWhite - The wide white logo image url.
 * @property {string} primaryColor - The primary color of the app.
 * @property {string} secondaryColor - The secondary color of the app.
 * @property {string} landingQuote - The landing quote.
 * @property {string[]} programBenefits - The program benefits.
 * @property {Socials} socials - The social media urls.
 */

/**
 * @typedef {object} AppSettings
 * @property {string} appId - The application id, as determined by the current localisation.
 * @property {Lang} lang - The localisation object.
 */

/**
 * Custom React hook for accessing global app information within the `SettingsProvider` context.
 *
 * @returns {AppSettings} An object containing the global app settings.
 */
const useSettings = () => {
  const context = useContext(settingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }

  const { loaded, appId, lang, appInfo, fetchSettings } = context;

  useEffect(() => {
    if (!loaded) fetchSettings();
  }, [fetchSettings, loaded]);

  return {
    loaded,
    appId,
    lang,
    appInfo,
  };
};

SettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { SettingsProvider, useSettings };
